import React from "react";
import { cloneDeep } from "lodash";

import { defaultUtilityCosts } from "utils/utilityData/defaultCosts";

import Button from "components/Button";
import Input from "components/Input";
import InputRow from "components/Input/Row";
import Select from "components/Input/Select";
import Field from "components/Field";
import FuelPrices from "features/Model/General/FuelPrices";
import TimeOfUse from "features/Model/General/TimeOfUse";

import { ACCESSOR, defaultTimeOfUse } from "../hybridAnalysis";

import classes from "./styles.module.scss";

const rateTypeOptions = [
    { value: "flat", label: "Flat rate" },
    { value: "timeOfUse", label: "Time-of-use" },
];

const Costs = ({ ready, hybridAnalysis, update, modelFuelPrices, modelTimeOfUse }) => {
    const timeOfUseSelected = hybridAnalysis?.electricityRateType === "timeOfUse";

    const resetPrices = () => {
        update(`${ACCESSOR}.fuelPrices`, cloneDeep(modelFuelPrices || defaultUtilityCosts));
        update(`${ACCESSOR}.timeOfUse`, cloneDeep(modelTimeOfUse || defaultTimeOfUse()));
    };

    return (
        <div className={classes.panel}>
            {ready && hybridAnalysis ? (
                <>
                    <div className={classes.block}>
                        <h3 className={classes.sectionTitle}>Utility costs</h3>
                        <p className={classes.sectionCopy}>
                            Edits here do not change the source model. Changing a price recalculates cost metrics from
                            the stored simulation results. Wood hybrid systems are not supported.
                        </p>
                        <InputRow gridTemplate="1fr 1fr 1fr">
                            <Select
                                name="hybridElectricityRateType"
                                options={rateTypeOptions}
                                label="Electricity rate"
                                placeholder="Flat rate"
                                input={{
                                    value: hybridAnalysis.electricityRateType || "flat",
                                    onChange: (value) => update(`${ACCESSOR}.electricityRateType`, value),
                                }}
                                info="Flat rate uses the electricity variable rate below. Time-of-use uses the schedule and period prices in the next section, on top of the hourly whole-home electrical load."
                            />
                        </InputRow>
                        <p className={classes.sectionCopy}>
                            Fuel prices entered should be the all-in fuel costs, including delivery and regulatory
                            charges where applicable.
                        </p>
                        {timeOfUseSelected && (
                            <p className={classes.sectionCopy}>
                                The displayed electricity variable rate is the average of the time-of-use prices across
                                every hour of the year. The fixed monthly charge, rebate, and tax still apply.
                            </p>
                        )}

                        <FuelPrices
                            accessor={`${ACCESSOR}.fuelPrices`}
                            change={update}
                            hiddenFuels={["wood"]}
                            electricityVariableRateDisabled={timeOfUseSelected}
                            electricityVariableRateDecimals={timeOfUseSelected ? 3 : undefined}
                        />
                        <div className={classes.actions}>
                            <Button type="hollow" onClick={resetPrices}>
                                Reset prices from General tab
                            </Button>
                        </div>
                    </div>
                    {timeOfUseSelected && (
                        <div className={classes.block}>
                            <h3 className={classes.sectionTitle}>Time-of-use schedule</h3>
                            <p className={classes.sectionCopy}>Select a preset schedule or enter a custom schedule.</p>
                            <TimeOfUse accessor={`${ACCESSOR}.timeOfUse`} change={update} />
                        </div>
                    )}
                    <div className={classes.block}>
                        <h3 className={classes.sectionTitle}>Equipment cost</h3>
                        <p className={classes.sectionCopy}>
                            Optional. Total cost of ownership adds the heat pump cost and the furnace cost to annual
                            operating cost multiplied by the lifetime. The default lifetime is 15 years. Leave a cost at
                            0 when you do not want it included.
                        </p>
                        <InputRow gridTemplate="1fr 1fr 1fr">
                            <Field
                                component={Input}
                                type="number"
                                name={`${ACCESSOR}.capitalCosts.heatPump`}
                                label="Heat pump ($)"
                                placeholder="0"
                                change={update}
                            />
                            <Field
                                component={Input}
                                type="number"
                                name={`${ACCESSOR}.capitalCosts.furnace`}
                                label="Furnace ($)"
                                placeholder="0"
                                change={update}
                            />
                            <Field
                                component={Input}
                                type="number"
                                name={`${ACCESSOR}.lifetimeYears`}
                                label="Lifetime (years)"
                                placeholder="15"
                                change={update}
                            />
                        </InputRow>
                    </div>
                </>
            ) : (
                <p className={classes.empty}>Loading analysis inputs…</p>
            )}
        </div>
    );
};

export default Costs;
